/**
 * Dismissed integrations store.
 *
 * Persists the slugs of Composio services the user does not want to be
 * prompted about. Stored in localStorage as a flat object: { slack: true, ... }.
 *
 * The dismissed list is also injected into AgentRunInput.sessionContext at
 * conversation start so the agent loop can include it in the system prompt
 * and avoid suggesting those services proactively.
 */

const STORAGE_KEY = 'river:dismissed_integrations';

function readRaw(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeRaw(obj: Record<string, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // localStorage unavailable — silent no-op
  }
}

/** Returns true if the user has chosen "Don't suggest again" for this slug. */
export function isDismissed(slug: string): boolean {
  return readRaw()[slug.toLowerCase()] === true;
}

/** Permanently dismiss a slug. Persists across sessions. */
export function dismissForever(slug: string): void {
  const obj = readRaw();
  obj[slug.toLowerCase()] = true;
  writeRaw(obj);
}

/** Returns the full list of dismissed slugs (for injecting into system prompt). */
export function getDismissedSlugs(): string[] {
  const obj = readRaw();
  return Object.keys(obj).filter(k => obj[k]);
}

/** Remove a dismissal (in case the user wants to undo via Settings). */
export function undismiss(slug: string): void {
  const obj = readRaw();
  delete obj[slug.toLowerCase()];
  writeRaw(obj);
}
